import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { SuccessModal } from "./SuccessModal";
import { formatErr } from "../lib/api";
import { SeatCounter } from "./SeatCounter";
import { STATS_EVENT } from "../hooks/useStats";
import { useT } from "../lib/prefs";
import { fireConfetti } from "../lib/confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fields = [
  { name: "nama", type: "text", testid: "input-nama", ac: "name" },
  { name: "phone", type: "tel", testid: "input-phone", ac: "tel" },
  { name: "email", type: "email", testid: "input-email", ac: "email" },
  { name: "telegram", type: "text", testid: "input-telegram", ac: "off" },
];

const empty = { nama: "", phone: "", email: "", telegram: "" };

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const TG_RE = /^@?[A-Za-z][A-Za-z0-9_]{3,30}[A-Za-z0-9]$/;

const validate = (f, m) => {
  const e = {};
  if (f.nama.trim().length < 2) e.nama = m.nama;
  if (f.phone.replace(/\D/g, "").length < 9) e.phone = m.phone;
  if (!EMAIL_RE.test(f.email.trim())) e.email = m.email;
  if (!TG_RE.test(f.telegram.trim())) e.telegram = m.telegram;
  return e;
};

export const BookingForm = () => {
  const t = useT();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [joinToken, setJoinToken] = useState("");
  const [refCode, setRefCode] = useState("");

  const onBlur = (name) => {
    if (!form[name]) return;
    setErrors((p) => ({ ...p, [name]: validate(form, t.errors)[name] }));
  };

  const onChange = (name, value) => {
    const next = { ...form, [name]: value };
    setForm(next);
    if (errors[name]) setErrors((p) => ({ ...p, [name]: validate(next, t.errors)[name] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form, t.errors);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
      const ref = (() => { try { return localStorage.getItem("rt_ref") || undefined; } catch { return undefined; } })();
      const { data } = await axios.post(`${API}/bookings`, { ...payload, ref });
      try { localStorage.removeItem("rt_ref"); } catch {}
      setRefCode(data?.ref_code ?? "");
      if (data?.stats) window.dispatchEvent(new CustomEvent(STATS_EVENT, { detail: data.stats }));
      setJoinToken(data?.join_token ?? "");
      setForm(empty);
      setErrors({});
      setOpen(true);
      fireConfetti();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || t.submitFail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      noValidate
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="glass rounded-[2rem] p-6 sm:p-10 relative"
      data-testid="booking-form"
    >
      <div className="flex items-center justify-between mb-8 font-mono text-xs tracking-[0.25em] text-violet-200">
        <span>{t.formTitle}</span>
        <span className="text-emerald-300">● LIVE</span>
      </div>
      <div className="mb-8 pb-6 border-b border-violet-500/20">
        <SeatCounter compact />
      </div>
      <div className="space-y-6">
        {fields.map((f, i) => (
          <label key={f.name} className="block group">
            <span className="flex items-center gap-3 font-mono text-sm tracking-widest text-violet-200 mb-2">
              {t.fields[f.name][0].toUpperCase()}
            </span>
            <input
              type={f.type}
              name={f.name}
              autoComplete={f.ac}
              autoCapitalize={f.name === "nama" ? "words" : "none"}
              inputMode={f.name === "email" ? "email" : f.name === "phone" ? "tel" : "text"}
              placeholder={t.fields[f.name][1]}
              value={form[f.name]}
              onChange={(e) => onChange(f.name, e.target.value)}
              onBlur={() => onBlur(f.name)}
              aria-invalid={!!errors[f.name]}
              data-testid={f.testid}
              className={`w-full bg-transparent border-b-2 outline-none py-3 text-base sm:text-lg text-white placeholder:text-violet-200/40 transition-colors duration-300 ${
                errors[f.name] ? "border-pink-400 focus:border-pink-300" : "border-violet-400/30 focus:border-violet-300"
              }`}
            />
            {errors[f.name] && (
              <span className="block mt-2 text-sm text-pink-300" data-testid={`error-${f.name}`}>{errors[f.name]}</span>
            )}
          </label>
        ))}
      </div>
      <motion.button
        whileTap={{ scale: 0.97 }}
        type="submit"
        disabled={loading}
        data-testid="submit-booking-button"
        className="btn-neon mt-10 w-full rounded-full py-4 font-semibold text-base inline-flex items-center justify-center gap-3 disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t.submit} <ArrowUpRight className="w-5 h-5" /></>}
      </motion.button>
      <p className="mt-4 text-center text-xs text-violet-200/70">{t.formNote}</p>
      <SuccessModal open={open} onOpenChange={setOpen} joinToken={joinToken} refCode={refCode} />
    </motion.form>
  );
};
